import React, { useState } from 'react';
import { searchFlights, parseSearchResults } from '../services/api';

export default function SearchPanel({ onSearchComplete, onError, initialParams }) {
  const [tripType, setTripType] = useState(initialParams?.returnDate ? 'roundtrip' : 'roundtrip');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    origin: initialParams?.origin || '',
    destination: initialParams?.destination || '',
    departureDate: initialParams?.departureDate || '',
    returnDate: initialParams?.returnDate || '',
    adults: initialParams?.adults || 1,
    children: initialParams?.children || 0,
    cabinClass: initialParams?.cabinClass || '',
    maxStops: initialParams?.maxStops ?? '',
    airline: initialParams?.airline || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    onError(null);
    setLoading(true);

    try {
      const params = {
        origin: form.origin.toUpperCase(),
        destination: form.destination.toUpperCase(),
        departureDate: form.departureDate,
        returnDate: tripType === 'roundtrip' ? form.returnDate : undefined,
        adults: parseInt(form.adults) || 1,
        children: parseInt(form.children) || 0,
        cabinClass: form.cabinClass || undefined,
        maxStops: form.maxStops !== '' ? parseInt(form.maxStops) : undefined,
        airline: form.airline ? form.airline.toUpperCase() : undefined,
      };

      const rawResults = await searchFlights(params);
      const parsed = parseSearchResults(rawResults);

      if (parsed.length === 0) {
        onError('No flights found for your search criteria. Try different dates or airports.');
      }

      onSearchComplete(parsed, params);
    } catch (err) {
      const msg =
        err.response?.data?.details?.message ||
        err.response?.data?.error ||
        err.message ||
        'Search failed';
      onError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Get tomorrow as minimum departure date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="search-card">
      <h2>Search Flights</h2>
      <div className="info-banner">
        This prototype uses <strong>Sabre Bargain Finder Max v5</strong> (REST API) for air shopping
        and <strong>Booking Management API v1</strong> (REST API) for reservations. Configure your
        Sabre sandbox credentials in the <code>.env</code> file to make live API calls.
      </div>

      <div className="trip-type-toggle">
        <button
          className={`trip-type-btn ${tripType === 'roundtrip' ? 'active' : ''}`}
          onClick={() => setTripType('roundtrip')}
          type="button"
        >
          Round Trip
        </button>
        <button
          className={`trip-type-btn ${tripType === 'oneway' ? 'active' : ''}`}
          onClick={() => setTripType('oneway')}
          type="button"
        >
          One Way
        </button>
      </div>

      <form onSubmit={handleSearch}>
        <div className="search-row">
          <div className="form-group">
            <label>From</label>
            <input
              name="origin"
              type="text"
              placeholder="JFK"
              maxLength={3}
              value={form.origin}
              onChange={handleChange}
              required
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          <div className="form-group">
            <label>To</label>
            <input
              name="destination"
              type="text"
              placeholder="LAX"
              maxLength={3}
              value={form.destination}
              onChange={handleChange}
              required
              style={{ textTransform: 'uppercase' }}
            />
          </div>
          <div className="form-group">
            <label>Departure</label>
            <input
              name="departureDate"
              type="date"
              min={minDate}
              value={form.departureDate}
              onChange={handleChange}
              required
            />
          </div>
          {tripType === 'roundtrip' && (
            <div className="form-group">
              <label>Return</label>
              <input
                name="returnDate"
                type="date"
                min={form.departureDate || minDate}
                value={form.returnDate}
                onChange={handleChange}
                required={tripType === 'roundtrip'}
              />
            </div>
          )}
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }}></span>
                Searching...
              </>
            ) : (
              'Search'
            )}
          </button>
        </div>

        <div className="search-row-2">
          <div className="form-group">
            <label>Adults</label>
            <select name="adults" value={form.adults} onChange={handleChange}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Cabin Class</label>
            <select name="cabinClass" value={form.cabinClass} onChange={handleChange}>
              <option value="">Any</option>
              <option value="Y">Economy</option>
              <option value="S">Premium Economy</option>
              <option value="C">Business</option>
              <option value="F">First</option>
            </select>
          </div>
          <div className="form-group">
            <label>Max Stops</label>
            <select name="maxStops" value={form.maxStops} onChange={handleChange}>
              <option value="">Any</option>
              <option value="0">Nonstop</option>
              <option value="1">1 Stop</option>
              <option value="2">2 Stops</option>
            </select>
          </div>
          <div className="form-group">
            <label>Airline (optional)</label>
            <input
              name="airline"
              type="text"
              placeholder="e.g. AA, UA, DL"
              maxLength={2}
              value={form.airline}
              onChange={handleChange}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
